const BYTES_PER_MB = 1024 * 1024;

/**
 * Whether the file's type is allowed by the `accept` list (exact MIME types or `type/*` wildcards).
 * Some browsers report no type for formats like HEIC, so those fall back to the file extension.
 *
 * @param {File} file
 * @param {string[]} acceptedTypes An empty list accepts everything.
 * @returns {boolean}
 */
function isAcceptedType(file, acceptedTypes) {
    if (acceptedTypes.length === 0) {
        return true;
    }

    if (file.type !== '') {
        return acceptedTypes.some((accepted) =>
            accepted.endsWith('/*') ? file.type.startsWith(accepted.slice(0, -1)) : accepted === file.type,
        );
    }

    const extension = file.name.split('.').pop().toLowerCase();

    return acceptedTypes.some((accepted) => {
        const subtype = accepted.split('/')[1];

        return subtype === '*' || subtype === extension || (subtype === 'jpeg' && extension === 'jpg');
    });
}

/**
 * Drag-and-drop image input with previews. Expects the real `<input type="file">` as `x-ref="input"`
 * and reports Livewire's `livewire-upload-*` events through `isUploading` / `isUploaded`.
 *
 * Files of the wrong type or size are refused before they are uploaded: the previous selection
 * is kept, the message is shown under the field and in a toast.
 *
 * @param {{
 *     buttonLabel: string,
 *     wireModel?: string|null,
 *     multiple?: boolean,
 *     acceptedTypes?: string[],
 *     maxSizeMb?: number,
 *     messages?: { type: string, size: string, upload: string, title: string },
 * }} config
 *        `wireModel` is the Livewire property to empty when the selection is cleared.
 */
export default function imageUpload({
    buttonLabel,
    wireModel = null,
    multiple = false,
    acceptedTypes = [],
    maxSizeMb = 8,
    messages = { type: '', size: '', upload: '', title: '' },
}) {
    return {
        files: [],
        previews: [],
        isDragging: false,
        isUploading: false,
        isUploaded: false,
        wasRejectedByServer: false,
        error: '',

        get hasFiles() {
            return this.files.length > 0;
        },

        get fileLabel() {
            if (this.files.length === 0) {
                return buttonLabel;
            }

            return this.files.length === 1 ? this.files[0].name : `${this.files.length} صور محددة`;
        },

        get fileSizeLabel() {
            if (this.files.length === 0) {
                return '';
            }

            const totalMb = this.files.reduce((sum, file) => sum + file.size, 0) / BYTES_PER_MB;

            return this.files.length === 1 ? `${totalMb.toFixed(2)} MB` : `${totalMb.toFixed(2)} MB إجمالي`;
        },

        /**
         * Returns the message explaining why the files cannot be uploaded, or null when they can.
         *
         * @param {File[]} files
         * @returns {string|null}
         */
        findProblem(files) {
            if (files.some((file) => !isAcceptedType(file, acceptedTypes))) {
                return messages.type;
            }

            if (files.some((file) => file.size > maxSizeMb * BYTES_PER_MB)) {
                return messages.size;
            }

            return null;
        },

        /**
         * Runs in the capture phase of `change`, before Livewire's own listener on the input, so a
         * refused selection never reaches the upload.
         */
        validateSelection(event) {
            if (event.target !== this.$refs.input) {
                return;
            }

            const selected = Array.from(this.$refs.input.files);

            // Cancelling the picker empties the input; keep the previous selection instead.
            if (selected.length === 0) {
                event.stopPropagation();
                this.restoreInput();

                return;
            }

            const problem = this.findProblem(selected);

            if (problem === null) {
                return;
            }

            event.stopPropagation();
            this.restoreInput();
            this.reject(problem);
        },

        handleFiles(fileList) {
            this.files = Array.from(fileList);
            this.previews = [];
            this.error = '';

            this.files.forEach((file) => {
                const reader = new FileReader();
                reader.onload = (event) => this.previews.push(event.target.result);
                reader.readAsDataURL(file);
            });
        },

        drop(event) {
            this.isDragging = false;

            const dropped = Array.from(event.dataTransfer.files).slice(0, multiple ? undefined : 1);

            if (dropped.length === 0) {
                return;
            }

            const problem = this.findProblem(dropped);

            if (problem !== null) {
                this.reject(problem);

                return;
            }

            this.setInputFiles(dropped);
            this.$refs.input.dispatchEvent(new Event('change', { bubbles: true }));
        },

        clear(event) {
            event.preventDefault();
            event.stopPropagation();

            this.reset();

            if (wireModel) {
                this.$wire.set(wireModel, multiple ? [] : null);
            }
        },

        uploadStarted() {
            this.isUploading = true;
            this.isUploaded = false;
            this.wasRejectedByServer = false;
        },

        uploadFinished() {
            this.isUploading = false;
            this.isUploaded = !this.wasRejectedByServer && this.hasFiles;
        },

        /** The upload itself failed (wrong type, too large, network): drop the selection. */
        uploadFailed() {
            this.reset();
            this.reject(messages.upload);
        },

        /** The server refused the uploaded file; it already shows its own message and toast. */
        rejectedByServer(detail) {
            if (!wireModel || detail?.property !== wireModel) {
                return;
            }

            this.wasRejectedByServer = true;
            this.reset();
        },

        reject(message) {
            this.error = message;

            window.toastMagic?.error(messages.title, message, true);
        },

        reset() {
            this.files = [];
            this.previews = [];
            this.error = '';
            this.isUploading = false;
            this.isUploaded = false;
            this.$refs.input.value = '';
        },

        /** Puts the last accepted selection back into the real input without triggering an upload. */
        restoreInput() {
            if (this.files.length === 0) {
                this.$refs.input.value = '';

                return;
            }

            this.setInputFiles(this.files);
        },

        setInputFiles(files) {
            const transfer = new DataTransfer();
            files.forEach((file) => transfer.items.add(file));

            this.$refs.input.files = transfer.files;
        },
    };
}
